import { useEffect, useRef, useCallback } from 'react';
import { autosave } from '../services/annotationApi';

interface UseAutoSaveOptions {
  taskId: string | undefined;
  getState: () => object;
  onStatusChange: (
    status: 'idle' | 'saving' | 'saved' | 'error',
    savedAt?: Date,
  ) => void;
  intervalMs?: number;
  enabled?: boolean;
}

export function useAutoSave({
  taskId,
  getState,
  onStatusChange,
  intervalMs = 10_000,
  enabled = true,
}: UseAutoSaveOptions) {
  // Keep latest references stable without re-running effects
  const getStateRef = useRef(getState);
  const onStatusChangeRef = useRef(onStatusChange);
  const isSavingRef = useRef(false);

  useEffect(() => {
    getStateRef.current = getState;
  });

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  });

  const saveNow = useCallback(async () => {
    if (!taskId) return;
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    onStatusChangeRef.current('saving');

    try {
      const stateSnapshot = getStateRef.current();
      const result = await autosave(taskId, stateSnapshot);
      const savedAt = result.savedAt ? new Date(result.savedAt) : new Date();
      onStatusChangeRef.current('saved', savedAt);
    } catch {
      onStatusChangeRef.current('error');
    } finally {
      isSavingRef.current = false;
    }
  }, [taskId]);

  // Auto-save on interval
  useEffect(() => {
    if (!enabled || !taskId) return;
    const interval = setInterval(saveNow, intervalMs);
    return () => clearInterval(interval);
  }, [enabled, taskId, intervalMs, saveNow]);

  // Listen for Ctrl+S to trigger a manual save
  useEffect(() => {
    if (!enabled || !taskId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        saveNow();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, taskId, saveNow]);

  return { saveNow };
}
